import { t } from "@travily/i18n";
import { CardCarousel } from "@ui/components/CardCarousel";
import { CardGrid } from "@ui/components/CardGrid";
import { EmptyState } from "@ui/components/EmptyState";
import { Heading } from "@ui/components/Heading";
import { OutlineButton } from "@ui/components/OutlineButton";
import { type ReactNode, useState } from "react";

const DEFAULT_PER_PAGE = 3;

export interface ListingSectionProps<Item> {
  /** Full-width "Add <thing>" button label shown from md up. */
  addLabel: string;
  /** Compact "Add" button label shown below md. */
  addShortLabel: string;
  emptyDescription: string;
  emptyIllustration: ReactNode;
  emptyTitle: string;
  items: Item[];
  onAdd?: () => void;
  /** Desktop page size for the "View more" pagination. */
  perPage?: number;
  /** Renders one card; must set a stable `key` on the returned element. */
  renderCard: (item: Item) => ReactNode;
  title: string;
}

/**
 * Section shell shared by the store panel's listing sections (Products,
 * Services): responsive heading with add actions, mobile carousel, desktop
 * paginated grid, and an empty state.
 */
export function ListingSection<Item>({
  addLabel,
  addShortLabel,
  emptyDescription,
  emptyIllustration,
  emptyTitle,
  items,
  onAdd,
  perPage = DEFAULT_PER_PAGE,
  renderCard,
  title,
}: ListingSectionProps<Item>) {
  const [visibleCount, setVisibleCount] = useState(perPage);
  const hasMore = items.length > visibleCount;
  const desktopItems = items.slice(0, visibleCount);

  return (
    <div className="flex w-full flex-col gap-4 lg:gap-6 md:text-left">
      <Heading.Root divider="responsive">
        <Heading.Title>{title}</Heading.Title>
        {onAdd ? (
          <Heading.Actions>
            <OutlineButton className="md:hidden" onPress={onAdd} size="36">
              {addShortLabel}
            </OutlineButton>
            <OutlineButton className="hidden md:inline-flex" onPress={onAdd} size="40">
              {addLabel}
            </OutlineButton>
          </Heading.Actions>
        ) : null}
      </Heading.Root>
      {items.length > 0 ? (
        <>
          <div className="md:hidden">
            <CardCarousel>{items.map(renderCard)}</CardCarousel>
          </div>
          <div className="hidden md:block">
            <CardGrid capCardWidth>{desktopItems.map(renderCard)}</CardGrid>
          </div>
          {hasMore && (
            <OutlineButton
              className="hidden md:inline-flex md:self-center"
              onPress={() => setVisibleCount((prev) => prev + perPage)}
            >
              {t("common.actions.view_more")}
            </OutlineButton>
          )}
        </>
      ) : (
        <EmptyState description={emptyDescription} illustration={emptyIllustration} title={emptyTitle} />
      )}
    </div>
  );
}
